import {gql, DocumentNode} from '@apollo/client';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import * as OrganizationActions from 'actions/OrganizationActions';
// @ts-expect-error
import * as UserActions from 'actions/UserActions';
import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {getDefaultDataPolicy} from 'auth/IntrinsicPolicies';
import {DATA_POLICY_SHAPE, DataPolicy} from 'auth/PolicyConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {NavStoreProject} from 'constants/SourceConstants';
import CreateSourceModal from 'create-components/CreateSourceModal';
import {getOwnerChoices} from 'create-components/OwnableUtil';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as FullScreenActions from 'nav/full-screen/FullScreenActions';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import notifications from 'notifications';
import OrganizationDAO from 'orgs/OrganizationDAO';
import {BenchlingState} from 'redux-stores/reduceApp';
import EntityStore from 'stores/EntityStore';
import TenantBreadcrumbs from 'tenants/TenantBreadcrumbs';
import getTestProps from 'test/lib/cypress/getTestProps';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {Header1} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as nyc from 'ui-library/NYC';
import SectionTabs from 'ui-library/SectionTabs';
import {ElementMouseEvent} from 'util/Types';

import {OrganizationPageQuery, OrganizationPageQueryVariables} from './__generated/OrganizationPageQuery';
import AddBenchlingAppInput from './AddBenchlingAppInput';
import AddMemberInput from './AddMemberInput';
import CreateUsersButton from './CreateUsersButton';
import {OrganizationFeatureBanner, OrganizationSignInBanner} from './OrganizationBanner';
import OrganizationBenchlingAppMembersTab from './OrganizationBenchlingAppMembersTab';
import OrganizationCalendarTab from './OrganizationCalendarTab';
import * as OrganizationConstants from './OrganizationConstants';
import OrganizationMembersTab from './OrganizationMembersTab';
import OrganizationProjectsTab from './OrganizationProjectsTab';
import OrganizationSettingsTab from './OrganizationSettingsTab';
import OrganizationTeamsTab from './OrganizationTeamsTab';
import {hasSentRequestForOrg} from './OrganizationUtil';

const {APPS_TAB, PROJECTS_TAB, CALENDAR_TAB, MEMBERS_TAB, SETTINGS_TAB, TEAMS_TAB} = OrganizationConstants;

const ORGANIZATION_TAB_TO_DISPLAY: {[tab: string]: string} = {
  [APPS_TAB]: 'Apps',
  [TEAMS_TAB]: 'Teams',
  [PROJECTS_TAB]: 'Projects',
  [CALENDAR_TAB]: 'Calendar',
  [MEMBERS_TAB]: 'Members',
  [SETTINGS_TAB]: 'Settings',
};

export const ORGANIZATION_TABS = [TEAMS_TAB, PROJECTS_TAB, CALENDAR_TAB, MEMBERS_TAB, APPS_TAB, SETTINGS_TAB];

export const ORGANIZATION_PAGE_QUERY: DocumentNode = gql`
  query OrganizationPageQuery($id: StrictString!) @engComponent(name: EXTERNAL_ADMIN) @skipAudit {
    organization(id: $id) {
      ... on OrganizationNode {
        id
        name
        handle
        avatarUrl
        admin
        member
        urlIdentifier
        permissions {
          admin
          canManageRoles
          canCreateTeams
        }
      }
      ...InaccessibleInfoGraphQL
    }
    currentTenant {
      id
      permissions {
        canCreateUsers
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ConnectedProps {
  allDataPolicies: Array<DataPolicy> | undefined;
}

function mapStateToProps(state: BenchlingState): ConnectedProps {
  return {
    allDataPolicies: state.models.policies.dataPolicies,
  };
}

export type OrganizationPageQueryOrganization = ExtractByTypename<
  OrganizationPageQuery['organization'],
  'OrganizationNode'
>;

export type OrganizationPageQueryTenant = ExtractByTypename<
  OrganizationPageQuery['currentTenant'],
  'TenantNode'
>;

export interface OrganizationPageProps {
  activeTab: string;
  isLoading: boolean;
  organization: OrganizationPageQueryOrganization;
  tenant: OrganizationPageQueryTenant;
  deprecatedOrganization: OrganizationConstants.Organization;
  organizationProjects: Array<NavStoreProject>;
  organizationMemberInvites: Array<OrganizationConstants.MemberInvite>;
  organizationMemberRequests: Array<OrganizationConstants.MemberRequest>;
  organizationMembers: Array<OrganizationConstants.Member>;
  organizationTeams: Array<OrganizationConstants.Team>;
  userIdToMemberInfo: {[userId: string]: OrganizationConstants.MemberInfo};
}

interface State {
  sentRequestToJoin: boolean;
}

class OrganizationPage extends React.Component<OrganizationPageProps & ConnectedProps, State> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    activeTab: PropTypes.oneOf(ORGANIZATION_TABS).isRequired,
    isLoading: PropTypes.bool.isRequired,
    deprecatedOrganization: OrganizationConstants.ORGANIZATION.isRequired,
    organizationProjects: PropTypes.arrayOf(PropTypes.object),
    organizationMemberInvites: PropTypes.arrayOf(OrganizationConstants.MEMBER_INVITE.isRequired),
    organizationMemberRequests: PropTypes.arrayOf(OrganizationConstants.MEMBER_REQUEST.isRequired),
    organizationMembers: PropTypes.arrayOf(OrganizationConstants.MEMBER.isRequired),
    organizationTeams: PropTypes.array,
    userIdToMemberInfo: PropTypes.object,

    // via connect
    allDataPolicies: PropTypes.arrayOf(DATA_POLICY_SHAPE.isRequired),
  };

  override state: State = {
    sentRequestToJoin: hasSentRequestForOrg(this.props.organization.id),
  };

  override componentDidMount(): void {
    analytics.track('organizations', 'loaded_organization_page', {
      organization_handle: this.props.organization.handle,
    });
  }

  _handleCreateProject = (): void => {
    assert(this.props.allDataPolicies != null, 'Expected data policies to be loaded to create project.');
    const defaultPolicy = getDefaultDataPolicy(this.props.allDataPolicies);

    legacyRunModal(CreateSourceModal, {
      initialCollaborators: [
        {...this.props.deprecatedOrganization, memberDataPolicyId: defaultPolicy.id, adminDataPolicyId: null},
      ],
      preferredOwnerId: this.props.organization.id,
      onCreateComplete: () => {
        OrganizationActions.fetchProjects(this.props.organization.id);
      },
      navigateOnSuccess: false,
    });
  };

  _handleOpenTab(tab: string): void {
    analytics.track('organizations', 'clicked_organization_page_tab', {
      tab,
      organization_handle: this.props.organization.handle,
    });
    OrganizationActions.updateActiveTab(this.props.organization.id, tab);
  }

  _handleRequestToJoin = (): void => {
    if (EntityStore.currentUser()) {
      UserActions.createInboundRequest(this.props.organization.id);
      this.setState({
        sentRequestToJoin: true,
      });
    } else {
      BenchlingUtil.redirectToSignin();
    }
  };

  _handleViewTemplates = (e: ElementMouseEvent): void => {
    FullScreenActions.hideScreen();
    BenchlingUtil.navigate(e);
  };

  override render(): JSX.Element {
    const isCurrentUserAdmin = this.props.organization.admin;
    const isCurrentUserMember = this.props.organization.member;
    const canManageRoles = this.props.organization.permissions.canManageRoles ?? false;
    const canCreateTeams = this.props.organization.permissions.canCreateTeams ?? false;

    // Keep visible tabs in sync with start/views.py::organization_page (/organization/<url_id>/<section>)
    const organizationTabs = [];
    if (isCurrentUserMember || canManageRoles) {
      organizationTabs.push(MEMBERS_TAB);
    }
    if (BenchlingConfig.ENABLE_TEAMS) {
      organizationTabs.push(TEAMS_TAB);
    }
    organizationTabs.push(PROJECTS_TAB);
    if (isCurrentUserMember) {
      organizationTabs.push(CALENDAR_TAB);
    }
    if (BenchlingConfig.ENABLE_BENCHLING_APPS && (isCurrentUserMember || canManageRoles)) {
      organizationTabs.push(APPS_TAB);
    }
    if (isCurrentUserMember || this.props.organization.permissions.admin) {
      organizationTabs.push(SETTINGS_TAB);
    }

    // eslint-disable-next-line no-restricted-syntax
    const userMembers = _.filter(
      this.props.organizationMembers,
      (member) => member.type === ModelTypeConstants.USER
    );
    // eslint-disable-next-line no-restricted-syntax
    const benchAppMembers = _.filter(
      this.props.organizationMembers,
      (member) => member.type === ModelTypeConstants.BENCHLING_APP
    );

    let createProjectDisabled = false;
    let createProjectTooltipTitle = 'Create project';
    if (getOwnerChoices().length === 0) {
      createProjectDisabled = true;
      createProjectTooltipTitle = 'Please contact your administrator to create a project';
    }

    return (
      <div
        className="organizationPage-container"
        {...getTestProps({component: 'OrganizationPage', element: 'root'})}
      >
        <nyc.Container size="lg">
          {EntityStore.currentUser() ? (
            <OrganizationFeatureBanner
              organizationName={this.props.organization.name}
              seenBannerPreference={OrganizationConstants.SEEN_FEATURE_BANNER_PREFERENCE}
            />
          ) : (
            <OrganizationSignInBanner organizationName={this.props.organization.name} />
          )}
          <nyc.Row className="organizationPage-headerContainer">
            <nyc.Col lg={1} />
            <nyc.Col lg={21}>
              <TenantBreadcrumbs />
              <div className="u-flex-display u-spacing-bottom">
                <Avatar
                  size={AvatarSize.XLARGE}
                  shape={AvatarShape.SQUARE}
                  entity={this.props.organization}
                />
                <div className="u-justify-content-center u-vertical-flex u-small-spacing-left">
                  <div className="u-tiny-spacing-bottom">
                    <Header1>{this.props.organization.name}</Header1>
                    <div className="u-faded">
                      Organization
                      {this.props.deprecatedOrganization.enableAdminView && this.props.organization.admin && (
                        <span>
                          {' | '}
                          <a
                            href={`/${this.props.organization.urlIdentifier}/admin`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {BenchlingConfig.ENABLE_TENANT_ADMIN_CONSOLE
                              ? 'View Activity'
                              : 'View Admin Dashboard'}
                          </a>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="u-faded">
                    {this.props.deprecatedOrganization.institution &&
                      this.props.deprecatedOrganization.institution.name}
                  </div>
                  <div className="u-faded">
                    <a
                      className="u-faded"
                      href={this.props.deprecatedOrganization.website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {this.props.deprecatedOrganization.website}
                    </a>
                    {this.props.deprecatedOrganization.website &&
                      this.props.deprecatedOrganization.location && <span> &middot; </span>}
                    {this.props.deprecatedOrganization.location}
                  </div>
                </div>
              </div>
              <div className="u-lr-split">
                <SectionTabs
                  tabs={organizationTabs.map((tabValue) => ({
                    handleClick: () => {
                      this._handleOpenTab(tabValue);
                    },
                    ariaControls: `${tabValue}_PANEL`,
                    id: tabValue,
                    isActive: this.props.activeTab === tabValue,
                    text: ORGANIZATION_TAB_TO_DISPLAY[tabValue],
                  }))}
                />
                <div className="organizationPage-topCta">
                  {(() => {
                    if (!isCurrentUserMember && !canManageRoles) {
                      return (
                        <Button
                          buttonStyle={ButtonStyle.SECONDARY}
                          disabled={this.state.sentRequestToJoin}
                          onClick={this._handleRequestToJoin}
                          size={ButtonSize.SMALL}
                          text={this.state.sentRequestToJoin ? 'Request sent' : 'Request to join'}
                        />
                      );
                    }
                    switch (this.props.activeTab) {
                      case PROJECTS_TAB:
                        return (
                          <div>
                            <a
                              onClick={this._handleViewTemplates}
                              href={TemplateCollectionDAO.collectionsURL()}
                              className="u-mini-spacing-right"
                            >
                              <Button
                                buttonStyle={ButtonStyle.TERTIARY_BLUE}
                                size={ButtonSize.SMALL}
                                text="View Templates"
                              />
                            </a>
                            <Tooltip
                              title={createProjectTooltipTitle}
                              placement={TooltipPlacement.BOTTOM}
                              container="body"
                            >
                              <Button
                                buttonStyle={ButtonStyle.SECONDARY}
                                onClick={this._handleCreateProject}
                                size={ButtonSize.SMALL}
                                text="Create project"
                                disabled={createProjectDisabled}
                              />
                            </Tooltip>
                          </div>
                        );
                      case TEAMS_TAB:
                        if (!canCreateTeams) {
                          return null;
                        }
                        return (
                          <div>
                            <a
                              href={OrganizationDAO.createTeamPageURL(this.props.organization.urlIdentifier)}
                              className="u-justify-content-end"
                            >
                              <Button
                                buttonStyle={ButtonStyle.SECONDARY}
                                size={ButtonSize.SMALL}
                                text="Create Team"
                              />
                            </a>
                          </div>
                        );
                      case MEMBERS_TAB:
                        if (!canManageRoles) {
                          return null;
                        }
                        return (
                          <div className="u-flex-display">
                            <AddMemberInput
                              organizationId={this.props.organization.id}
                              organizationMembers={this.props.organizationMembers}
                              organizationMemberInvites={this.props.organizationMemberInvites}
                              organizationMemberRequests={this.props.organizationMemberRequests}
                            />
                            {BenchlingConfig.ENABLE_USER_PROVISIONING &&
                              this.props.tenant.permissions.canCreateUsers && (
                                <CreateUsersButton
                                  organization={this.props.deprecatedOrganization}
                                  teams={this.props.organizationTeams}
                                />
                              )}
                          </div>
                        );
                      case APPS_TAB:
                        if (!canManageRoles) {
                          return null;
                        }
                        return (
                          <div className="u-flex-display">
                            <AddBenchlingAppInput
                              organizationId={this.props.organization.id}
                              organizationMembers={this.props.organizationMembers}
                            />
                          </div>
                        );
                      default:
                        return null;
                    }
                  })()}
                </div>
              </div>
            </nyc.Col>
          </nyc.Row>
          <nyc.Row
            aria-labelledby={this.props.activeTab}
            className="organizationPage-sectionContainer"
            id={`${this.props.activeTab}_PANEL`}
            role="tabpanel"
          >
            <nyc.Col lg={1} />
            <nyc.Col lg={21}>
              <div className="u-padding-top">
                {(() => {
                  switch (this.props.activeTab) {
                    case TEAMS_TAB:
                      return (
                        <OrganizationTeamsTab
                          organizationId={this.props.organization.id}
                          organizationName={this.props.organization.name}
                          organizationTeams={this.props.organizationTeams}
                        />
                      );
                    case PROJECTS_TAB:
                      return (
                        <OrganizationProjectsTab
                          isCurrentUserMember={isCurrentUserMember}
                          organization={this.props.deprecatedOrganization}
                          organizationProjects={this.props.organizationProjects}
                        />
                      );
                    case CALENDAR_TAB:
                      assert(isCurrentUserMember, 'User must be in org to view calendar tab.');
                      return <OrganizationCalendarTab organizationId={this.props.organization.id} />;
                    case MEMBERS_TAB:
                      return (
                        <OrganizationMembersTab
                          isCurrentUserMember={isCurrentUserMember}
                          canManageRoles={canManageRoles}
                          organization={this.props.organization}
                          organizationMemberInvites={this.props.organizationMemberInvites}
                          organizationMemberRequests={this.props.organizationMemberRequests}
                          organizationMembers={userMembers}
                          userIdToMemberInfo={this.props.userIdToMemberInfo}
                        />
                      );
                    case APPS_TAB:
                      return (
                        <OrganizationBenchlingAppMembersTab
                          canManageRoles={canManageRoles}
                          isCurrentUserMember={isCurrentUserMember}
                          organization={this.props.organization}
                          organizationBenchlingAppMembers={benchAppMembers}
                          userIdToMemberInfo={this.props.userIdToMemberInfo}
                        />
                      );
                    case SETTINGS_TAB:
                      return (
                        <OrganizationSettingsTab
                          isCurrentUserAdmin={isCurrentUserAdmin}
                          isLoading={this.props.isLoading}
                          organization={this.props.deprecatedOrganization}
                        />
                      );
                    default:
                      return null;
                  }
                })()}
              </div>
            </nyc.Col>
          </nyc.Row>
        </nyc.Container>
      </div>
    );
  }
}

const ConnectedOrganizationPage = connect(mapStateToProps)(OrganizationPage);

export default function QueryingOrganizationPage(
  props: Omit<OrganizationPageProps, 'organization' | 'tenant'>
): JSX.Element | null {
  const result = useBenchlingQuery<OrganizationPageQuery, OrganizationPageQueryVariables>(
    ORGANIZATION_PAGE_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        id: props.deprecatedOrganization.id,
      },
    }
  );
  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load organization. Try reloading the page.');
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (isInaccessible(result.data.organization)) {
    notifications.error('Could not load organization. Try reloading the page.');
    return null;
  } else {
    return (
      <ConnectedOrganizationPage
        organization={result.data.organization}
        tenant={result.data.currentTenant}
        {...props}
      />
    );
  }
}
